import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchBroadcastStatus, isLinkGoneError } from "../../lib/broadcastApi";
import {
  SceneTransitionHost,
  clampTransitionMs,
} from "./animation/sceneTransition";
import Stage, { useWindowSize } from "./Stage";
import StageWidgets from "./StageWidgets";
import UnavailableFrame from "./UnavailableFrame";
import { useBroadcastDocument } from "./useBroadcastDocument";
import { useBroadcastFeed } from "./useBroadcastFeed";

/** How often an Off page asks `/broadcast/status` whether it is On again. */
export const OFF_POLL_MS = 60_000;

type Air = "checking" | "on" | "off" | "unavailable";

/** Live path: state fetch, socket, reconcile. Mounted ONLY while On. */
function LiveBroadcast({
  token,
  onOff,
  onUnavailable,
}: {
  token: string;
  onOff: () => void;
  onUnavailable: () => void;
}) {
  const fit = useWindowSize();
  const feed = useBroadcastFeed(token);

  useEffect(() => {
    if (feed.status === "off") onOff();
    else if (feed.status === "unavailable") onUnavailable();
  }, [feed.status, onOff, onUnavailable]);

  if (feed.status === "unavailable") return <UnavailableFrame />;
  if (feed.status === "off" || !feed.layout) {
    return (
      <div
        data-testid="broadcast-loading"
        style={{ position: "fixed", inset: 0 }}
      />
    );
  }
  // A Take changes `transition.key`: the host keeps the previous stage
  // mounted next to the new one for the transition, then drops it.
  return (
    <div style={{ position: "fixed", inset: 0 }}>
      <SceneTransitionHost
        sceneKey={String(feed.transition.key)}
        type={feed.transition.type}
        durationMs={clampTransitionMs(feed.transition.durationMs)}
      >
        <Stage
          stage={feed.layout.stage}
          branding={feed.broadcaster}
          fit={fit}
          assets={feed.assets}
        >
          <StageWidgets layout={feed.layout} feed={feed} onAir />
        </Stage>
      </SceneTransitionHost>
    </div>
  );
}

/**
 * `/broadcast/:token`: the OBS browser-source page. No chrome, no auth.
 * While the link is Off it renders an empty transparent stage, opens no
 * socket and only polls `GET /broadcast/status` every 60 s. 401/403 (or a 1008
 * socket close) end all polling.
 */
export default function BroadcastPage() {
  const { token = "" } = useParams<{ token: string }>();
  useBroadcastDocument(true, true);
  const [air, setAir] = useState<Air>("checking");

  // Poll only while checking / Off. Checking -> Off keeps the same interval.
  const polling = air === "checking" || air === "off";
  // Only the first probe is immediate. After the live page falls back to Off
  // the next probe waits a full interval, so an inconsistent backend can never
  // flap On/Off in a tight loop.
  const airRef = useRef(air);
  airRef.current = air;
  useEffect(() => {
    if (!token || !polling) return;
    let cancelled = false;
    const check = async () => {
      try {
        const status = await fetchBroadcastStatus(token);
        if (!cancelled) setAir(status.on_air ? "on" : "off");
      } catch (err) {
        if (!cancelled && isLinkGoneError(err)) setAir("unavailable");
        // Any other failure: stay as is; the next tick retries.
      }
    };
    if (airRef.current === "checking") void check();
    const id = setInterval(check, OFF_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, polling]);

  if (air === "unavailable") return <UnavailableFrame />;
  if (air === "on") {
    return (
      <LiveBroadcast
        token={token}
        onOff={() => setAir("off")}
        onUnavailable={() => setAir("unavailable")}
      />
    );
  }
  return (
    <div
      data-testid={air === "off" ? "broadcast-off" : "broadcast-loading"}
      style={{ position: "fixed", inset: 0, background: "transparent" }}
    />
  );
}
